```vue
<!-- app/app.vue -->
<script setup lang="ts">
/**
 * Root application component
 * Handles global head configuration and layout structure
 */

// Get canonical site URL from single source of truth (ensure no trailing slash)
const siteUrl = useSiteUrl().replace(/\/+$/, '')

/**
 * Environment-aware default meta robots directive.
 *
 * Mirrors the prod-vs-dev branching in server/middleware/robots.ts so the
 * X-Robots-Tag HTTP header and the <meta name="robots"> tag emit the same
 * positive signal on production and the same defensive noindex on dev/staging.
 *
 * Defensive layer for stale "Excluded by 'noindex' tag" GSC verdicts:
 * GSC's URL Inspection tool surfaces the rendered meta tag (not headers),
 * so an explicit positive directive here helps overcome cached
 * BLOCKED_BY_META_TAG verdicts at the next crawl.
 *
 * Page-level useHead calls (error.vue, checkout pages) override this by
 * declaring the same meta name later in the render cycle.
 */
const siteEnv = (useRuntimeConfig().public.siteEnv as string).toLowerCase()
const isProduction = ['production', 'prod', 'www'].includes(siteEnv)
const robotsDirective = isProduction ? 'index, follow' : 'noindex, nofollow, noarchive'

/**
 * Global canonical URL generation
 *
 * Canonical policy:
 * - canonicalHref = siteUrl + route.path
 * - Must NOT include query strings or hashes (route.path excludes these by design)
 * - Homepage gets trailing slash (route.path = '/'), other pages don't
 *
 * Query string policy:
 * We treat query params as non-indexable variants (tracking/selection).
 * If we later introduce index-worthy query pagination/facets (e.g. ?page=2),
 * we must revisit canonical policy (allowlist such params or redesign URLs).
 */
const route = useRoute()
const canonicalUrl = computed(() => `${siteUrl}${route.path}`)

/**
 * Global Organization schema - injected once on every page.
 * Page-level schemas (WebPage, Service, Article) reference this via:
 *   publisher: { "@id": "https://www.metwide.com.au/#organization" }
 *   provider:  { "@id": "https://www.metwide.com.au/#organization" }
 */
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${siteUrl}/#organization`,
  name: 'Metwide Communications',
  url: siteUrl,
  logo: `${siteUrl}/img/logo.svg`,
  description:
    'Metwide delivers B2B ICT solutions including Internet & Data, Cloud Services, Voice & Video, Managed IT, Security, Web Solutions, and Radio Services across Australia.',
  foundingDate: '2005',

  // Broad coverage (remote delivery + travel interstate as needed)
  areaServed: { '@type': 'Country', name: 'Australia' },

  // Preferred structured contact info
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'sales',
      telephone: '1300 300 210',
      email: 'sales@metwide.com.au',
      areaServed: 'AU',
      availableLanguage: ['en-AU'],
    },
  ],

  // Keep top-level fields too
  telephone: '1300 300 210',
  email: 'sales@metwide.com.au',

  // Only list real Metwide locations (Sydney + Gold Coast)
  address: [
    {
      '@type': 'PostalAddress',
      streetAddress: 'Unit 1 40 George St',
      addressLocality: 'Granville',
      addressRegion: 'NSW',
      postalCode: '2142',
      addressCountry: 'AU',
    },
    {
      '@type': 'PostalAddress',
      streetAddress: 'Unit 2/3, Dalton Street',
      addressLocality: 'Upper Coomera',
      addressRegion: 'QLD',
      postalCode: '4209',
      addressCountry: 'AU',
    },
  ],

  sameAs: ['https://www.linkedin.com/company/metwide-communications-pty-ltd'],

  // Only keep if accurate/current (structured data should be factual)
  hasCredential: [
    {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'license',
      name: 'NSW Master Security Licence',
      recognizedBy: {
        '@type': 'Organization',
        name: 'Security Industry Registry Authority (SIRA)',
      },
    },
    {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'license',
      name: 'QLD Master Security Licence',
      recognizedBy: {
        '@type': 'Organization',
        name: 'Queensland Office of Fair Trading',
      },
    },
    {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'certification',
      name: 'ISO 9001 Quality Management',
    },
    {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'certification',
      name: 'ISO 14001 Environmental Management',
    },
    {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'certification',
      name: 'ISO 45001 Occupational Health & Safety',
    },
  ],
}

/**
 * Sydney HQ LocalBusiness schema - injected once on every page.
 *
 * Separate from Organization on purpose: Organization is the corporate entity
 * (national, Australia-wide), LocalBusiness is the Granville office. Linked
 * via parentOrganization so search engines can associate the two.
 *
 * Credentials are scoped to NSW + the ISO certifications that apply
 * company-wide. The QLD Master Security Licence sits on the Organization
 * node only; it does not belong on the Sydney office record.
 *
 * geo and openingHoursSpecification carry the coordinates and hours
 * confirmed by Metwide stakeholders for the Granville office. priceRange
 * is still omitted pending price signoff; do not reintroduce a placeholder.
 *
 * Upper Coomera (QLD) is not added here; it should ship as a second
 * LocalBusiness node alongside the QLD page work, not piggyback on Sydney.
 */
const granvilleLocalBusinessCredentials = organizationSchema.hasCredential.filter(
  (c) => c.name !== 'QLD Master Security Licence',
)

const granvilleLocalBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${siteUrl}/#granville`,
  name: 'Metwide Communications (Sydney)',
  parentOrganization: { '@id': `${siteUrl}/#organization` },
  url: siteUrl,
  image: `${siteUrl}/img/logo.svg`,
  telephone: '1300 300 210',
  email: 'sales@metwide.com.au',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Unit 1 40 George St',
    addressLocality: 'Granville',
    addressRegion: 'NSW',
    postalCode: '2142',
    addressCountry: 'AU',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -33.83218,
    longitude: 151.01764,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
  ],
  areaServed: 'Greater Sydney',
  hasCredential: granvilleLocalBusinessCredentials,
}

useHead({
  titleTemplate: (title) => {
    if (!title) return 'Metwide - Business ICT Solutions'
    if (title.endsWith(' | Metwide')) return title
    return `${title} | Metwide`
  },
  htmlAttrs: {
    lang: 'en-AU',
  },
  meta: [
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { key: 'robots', name: 'robots', content: robotsDirective },
  ],
  link: [
    {
      key: 'canonical', // Dedupe key: ensures only one canonical tag even if accidentally added elsewhere
      rel: 'canonical',
      href: canonicalUrl,
    },
  ],

  script: [
    // Global Organization schema
    {
      key: 'organization-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify(organizationSchema),
    },
    // Sydney HQ LocalBusiness schema
    {
      key: 'granville-localbusiness-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify(granvilleLocalBusinessSchema),
    },
  ],
})

/**
 * Site-wide social-share defaults.
 *
 * twitter:card is required to force the large-image layout; once set,
 * Twitter/X falls back to og:title / og:description / og:image
 * automatically, so we don't duplicate those into twitter:* tags.
 *
 * og:url and og:image are emitted as absolute URLs. Page-level
 * useSeoMeta() calls override individual fields; per-page og:image
 * overrides may still emit a relative path.
 */
useSeoMeta({
  twitterCard: 'summary_large_image',
  ogUrl: canonicalUrl,
  ogImage: `${siteUrl}/og-image.jpg`,
})
</script>

<template>
  <div>
    <NuxtRouteAnnouncer />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>
```
